import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { RelayType } from '@diritto/common/content/constants';
import { SectionDto } from '@diritto/common/home/dtos/section.dtos';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ContentListType } from '@diritto/common/tracker/tracking-event.constant';
import { accurateItems, shuffle } from '@diritto/common/utils/array';
import { LatestContentItem } from 'app-components/latest/LatestContentItem';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetSectionContentInfiniteQuery } from 'queries/relay/useGetRelaySectionContentInfinityQuery';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { EmptyList } from 'ui-components/common/EmptyList';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { useClient } from 'ui-hooks/useClient';
import { useHomeSettingState } from 'contexts/HomeSettingProvider';

interface SectionContentListProps {
  contentIds: number[];
  contents: Record<number | string, ContentRelayDto>;
  profiles: Record<number | string, ProfileLiteRelayDto>;
  selectedCategoryId?: number;
  emptyText?: string;
  listType?: ContentListType;
}

export const SectionContentList = ({ section }: { section: SectionDto }) => {
  const { includeAdult: userIncludeAdult, sessionSeed } = useAuthState();
  const client = useClient();
  const { categoryIds } = useHomeSettingState();
  const includeAdult = client.isApp ? userIncludeAdult : true;

  const essentialCategoryIds = useMemo(
    () => (section.isEssentialVisible ? [] : categoryIds),
    [section.isEssentialVisible, categoryIds],
  );

  const { isLoading, data, hasNextPage, fetchNextPage } = useGetSectionContentInfiniteQuery(section.slug, {
    includeAdult,
    categoryIds: essentialCategoryIds,
  });

  const { contentIds, contents, profiles } = useMemo(
    () => ({
      contentIds: data?.pages.flatMap(it => it.section.contentIds) ?? [],
      contents: accurateItems<ContentRelayDto>('contents', data?.pages),
      profiles: accurateItems<ProfileLiteRelayDto>('profileLites', data?.pages) ?? {},
    }),
    [data?.pages],
  );

  const rearrangedContentIds = useMemo(
    () => (section.shufflePreviewItem ? shuffle(contentIds, sessionSeed).slice(0, contentIds.length) : contentIds),
    [contentIds, section.shufflePreviewItem, sessionSeed],
  );

  return (
    <InfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
      {!isLoading && <ContentList contentIds={rearrangedContentIds} contents={contents} profiles={profiles} />}
    </InfiniteScrollWrapper>
  );
};

const ContentList = ({
  contentIds,
  contents,
  profiles,
  selectedCategoryId,
  emptyText = '아직 작품이 없어요.',
  listType = ContentListType.LatestUpdated,
}: SectionContentListProps) => {
  const blockedProfileIds = useBlockProfiles();
  if (contentIds.length === 0) {
    return <EmptyList description={emptyText} />;
  }

  return (
    <StyledList>
      {contentIds.map((id, idx) => {
        const content: ContentRelayDto = contents[id];
        const profile =
          content?.relayType === RelayType.Public && content.profileId ? profiles[content.profileId] : undefined;

        if ((profile && profile.isDeleted) || !content || content.relayType !== RelayType.Public) {
          return null;
        }

        if (profile && blockedProfileIds.has(profile.id)) {
          return null;
        }

        return (
          <StyledItem key={id}>
            <LatestContentItem
              key={id}
              listItemIndex={idx}
              listType={listType}
              content={content}
              profile={profile}
              selectedCategoryId={selectedCategoryId}
            />
          </StyledItem>
        );
      })}
    </StyledList>
  );
};

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px;
`;

const StyledItem = styled.div`
  position: relative;
`;
